/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { lazy, Suspense } from 'react';

import { jsx } from '@compiled/react';
import { ErrorBoundary } from 'react-error-boundary';

import { I18NMessages, IntlMessagesProvider } from '@atlaskit/intl-messages-provider';
import { fg } from '@atlaskit/platform-feature-flags';

import { asyncLanguages } from '../common/utils/locale/async-languages';
import defaultMessages from '../i18n/en';
import { type ModalDialogContentProps } from '../types';

import { ErrorState } from './error-state';
import { LoadingState } from './loading-state';
import { withModalDialog } from './modal-dialog';

const fetchMessages = async (locale: string): Promise<I18NMessages | undefined> => {
	try {
		// Fallback to the generic locale if specified one is not found
		const languageLocale =
			locale in asyncLanguages
				? locale
				: locale.replace(/-/g, '_') in asyncLanguages
					? locale.replace(/-/g, '_')
					: locale.split(/[-_]/)[0] || '';

		// Dynamic import of the locale file
		const localeMessages = (await asyncLanguages[languageLocale]?.()) as
			| { default: I18NMessages }
			| undefined;

		return localeMessages?.default ?? defaultMessages;
	} catch (e) {
		// ignore
	}

	return defaultMessages;
};

const LazyGlobalInviteModalDialogContent = lazy(() =>
	import(
		/* webpackChunkName: "@atlaskit-internal_global-invite--modal-dialog-content" */
		'./modal-dialog-content'
	).then((module) => ({
		default: module.ModalDialogContent,
	})),
);

function AsyncGlobalInviteModalDialogContent(props: ModalDialogContentProps) {
	const modalContent = (
		<ErrorBoundary fallback={<ErrorState />}>
			<Suspense fallback={<LoadingState />}>
				<LazyGlobalInviteModalDialogContent {...props} />
			</Suspense>
		</ErrorBoundary>
	);

	return fg('global_invite_uses_intl_provider') ? (
		<IntlMessagesProvider defaultMessages={defaultMessages} loaderFn={fetchMessages}>
			{modalContent}
		</IntlMessagesProvider>
	) : (
		modalContent
	);
}

export const GlobalInvite = withModalDialog(AsyncGlobalInviteModalDialogContent);
